import { useState, useEffect } from 'react';
import { getAggregates, getPrice, getDetails } from '../services/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './StockChart.css';

function getYTDDays() {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  return Math.ceil((now - jan1) / (1000 * 60 * 60 * 24));
}

const RANGES = [
  { label: '1D', days: 1, timespan: 'minute', multiplier: 5 },
  { label: '1W', days: 7, timespan: 'day', multiplier: 1 },
  { label: '1M', days: 30, timespan: 'day', multiplier: 1 },
  { label: '3M', days: 90, timespan: 'day', multiplier: 1 },
  { label: 'YTD', days: getYTDDays(), timespan: 'day', multiplier: 1 },
  { label: '1Y', days: 365, timespan: 'day', multiplier: 1 },
  { label: '5Y', days: 1825, timespan: 'week', multiplier: 1 },
  { label: 'MAX', days: 18250, timespan: 'month', multiplier: 1 },
];

function formatDate(timestamp, range) {
  const d = new Date(timestamp);
  if (range <= 1) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  if (range <= 7) return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (range <= 30) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function formatNum(num) {
  if (!num) return '--';
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
}

function formatVol(num) {
  if (!num) return '--';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toLocaleString();
}

export default function StockChart({ ticker, name, children, holdings = [], stocksValue = 0 }) {
  const [data, setData] = useState([]);
  const [range, setRange] = useState(RANGES[1]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - range.days);
        const toStr = to.toISOString().split('T')[0];
        const fromStr = from.toISOString().split('T')[0];

        const [aggData, priceData] = await Promise.all([
          getAggregates(ticker, fromStr, toStr, range.timespan, range.multiplier),
          getPrice(ticker),
        ]);

        const chartData = (aggData.results || []).map((r) => ({
          time: r.t,
          price: r.c,
          label: formatDate(r.t, range.days),
        }));
        setData(chartData);
        setPriceInfo(priceData);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticker, range]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const d = await getDetails(ticker);
        setDetails(d);
      } catch {
        setDetails(null);
      }
    };
    fetchDetails();
  }, [ticker]);

  const price = priceInfo?.price || (data.length > 0 ? data[data.length - 1].price : 0);
  const change = priceInfo?.change || 0;
  const changePct = priceInfo?.changePct || 0;
  const isPositive = change >= 0;
  const chartColor = isPositive ? '#10b981' : '#ef4444';

  const pe = (details?.eps && details.eps > 0 && price)
    ? (price / details.eps).toFixed(2)
    : null;

  return (
    <div className="stock-chart">
      <div className="chart-header">
        <div>
          <h2 className="chart-ticker">{ticker}</h2>
          <p className="chart-name">{details?.name || name}</p>
        </div>
        <div className="chart-price-info">
          <span className="chart-price">${price.toFixed(2)}</span>
          <span className={`chart-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}{change.toFixed(2)} ({changePct.toFixed(2)}%)
          </span>
        </div>
      </div>

      <div className="chart-range-buttons">
        {RANGES.map((r) => (
          <button
            key={r.label}
            className={range.label === r.label ? 'active' : ''}
            onClick={() => setRange(r)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="chart-container">
        {loading ? (
          <div className="chart-loading">Loading chart...</div>
        ) : data.length === 0 ? (
          <div className="chart-loading">No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id={`gradient-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={['auto', 'auto']}
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                tickFormatter={(v) => `$${v.toFixed(0)}`}
                width={60}
              />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9' }}
                formatter={(v) => [`$${v.toFixed(2)}`, 'Price']}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Area
                type="monotone"
                dataKey="price"
                stroke={chartColor}
                strokeWidth={2}
                fill={`url(#gradient-${ticker})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {children}

      <div className="stock-stats">
        <div className="stat-item">
          <span className="stat-label">Open</span>
          <span className="stat-value">{priceInfo?.open ? `$${priceInfo.open.toFixed(2)}` : '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Today's High</span>
          <span className="stat-value">{priceInfo?.high ? `$${priceInfo.high.toFixed(2)}` : '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Today's Low</span>
          <span className="stat-value">{priceInfo?.low ? `$${priceInfo.low.toFixed(2)}` : '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Volume</span>
          <span className="stat-value">{formatVol(priceInfo?.volume)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Market Cap</span>
          <span className="stat-value">{formatNum(details?.market_cap)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">P/E Ratio</span>
          <span className="stat-value">{pe || '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Dividend</span>
          <span className="stat-value">{details?.dividend ? `$${details.dividend.toFixed(2)}` : '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">52W High</span>
          <span className="stat-value">{details?.week52_high ? `$${details.week52_high.toFixed(2)}` : '--'}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">52W Low</span>
          <span className="stat-value">{details?.week52_low ? `$${details.week52_low.toFixed(2)}` : '--'}</span>
        </div>
      </div>

      {(() => {
        const holding = holdings.find(h => h.ticker === ticker);
        if (!holding) return null;
        const marketValue = price * holding.shares;
        const costBasis = holding.avg_price * holding.shares;
        const totalReturn = marketValue - costBasis;
        const totalReturnPct = costBasis > 0 ? (totalReturn / costBasis) * 100 : 0;
        const todayReturn = change * holding.shares;
        const todayReturnPct = changePct;
        const totalPortfolio = stocksValue;
        const diversity = totalPortfolio > 0 ? (marketValue / totalPortfolio) * 100 : 0;

        return (
          <div className="position-section">
            <h3 className="position-title">Your Position</h3>
            <div className="position-grid">
              <div className="position-item">
                <span className="stat-label">Shares</span>
                <span className="stat-value">{holding.shares}</span>
              </div>
              <div className="position-item">
                <span className="stat-label">Market Value</span>
                <span className="stat-value">${marketValue.toFixed(2)}</span>
              </div>
              <div className="position-item">
                <span className="stat-label">Avg Cost</span>
                <span className="stat-value">${holding.avg_price.toFixed(2)}</span>
              </div>
              <div className="position-item">
                <span className="stat-label">Portfolio Diversity</span>
                <span className="stat-value">{diversity.toFixed(1)}%</span>
              </div>
              <div className="position-item">
                <span className="stat-label">Today's Return</span>
                <span className={`stat-value ${todayReturn >= 0 ? 'pos' : 'neg'}`}>
                  {todayReturn >= 0 ? '+' : ''}${todayReturn.toFixed(2)} ({todayReturnPct >= 0 ? '+' : ''}{todayReturnPct.toFixed(2)}%)
                </span>
              </div>
              <div className="position-item">
                <span className="stat-label">Total Return</span>
                <span className={`stat-value ${totalReturn >= 0 ? 'pos' : 'neg'}`}>
                  {totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(2)} ({totalReturnPct >= 0 ? '+' : ''}{totalReturnPct.toFixed(2)}%)
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
